import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import "./UploadYoutubeLink.css";
import { Tooltip } from "react-tooltip";

/* ---------------- URL helpers ---------------- */

// "90" | "90s" | "1m30s" | "1h2m3s" -> seconds
function parseStart(raw) {
    if (!raw) return 0;
    if (/^\d+$/.test(raw)) return parseInt(raw, 10);
    const m = raw.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?/i);
    if (!m) return 0;
    return (
        parseInt(m[1] || 0, 10) * 3600 +
        parseInt(m[2] || 0, 10) * 60 +
        parseInt(m[3] || 0, 10)
    );
}

function parseVideoUrl(rawUrl) {
    const url = (rawUrl || "").trim();
    if (!url) return { valid: false, error: "Please paste a URL." };

    let parsed;
    try {
        parsed = new URL(url);
    } catch {
        return { valid: false, error: "That doesn't look like a valid URL." };
    }

    const host = parsed.hostname.replace(/^www\./, "");
    const isYoutube =
        host === "youtube.com" || host === "m.youtube.com" || host === "youtu.be";

    if (!isYoutube) {
        // generic website link — still allowed per "Drop YouTube Or Website Link"
        return {
            valid: true,
            type: "website",
            rawUrl: url,
            videoId: null,
            embedUrl: url,
            startSeconds: 0,
        };
    }

    let videoId = "";
  if (host === "youtu.be") {
    videoId = parsed.pathname.slice(1);
  } else if (parsed.pathname.startsWith("/embed/")) {
    videoId = parsed.pathname.split("/embed/")[1];
  } else if (parsed.pathname.startsWith("/shorts/")) {
    videoId = parsed.pathname.split("/shorts/")[1]; // youtube.com/shorts/ABC123
  } else if (parsed.pathname.startsWith("/live/")) {
    videoId = parsed.pathname.split("/live/")[1]; // youtube.com/live/ABC123
  } else {
    videoId = parsed.searchParams.get("v") || "";
  }
    videoId = (videoId || "").split("/")[0];

    if (!videoId) {
        return { valid: false, error: "Couldn't find a video id in that link." };
    }

    const startSeconds = parseStart(
        parsed.searchParams.get("t") || parsed.searchParams.get("start")
    );

    const qs = new URLSearchParams();
    if (startSeconds) qs.set("start", String(startSeconds));
    qs.set("enablejsapi", "1"); // so timestamp buttons can postMessage seek later
    const embedUrl = `https://www.youtube.com/embed/${videoId}?${qs.toString()}`;

    return { valid: true, type: "youtube", rawUrl: url, videoId, embedUrl: url, startSeconds };
}

/* ---------------- menu row (trigger) ---------------- */

export default function UploadYoutubeLink({ setShowUploadPop, onOpen, youtubeLinks }) {
    const handleClick = () => {
        if (youtubeLinks.length >= 1) return;
        // close the small upload popup, then open the modal mounted at FileUpload level
        setShowUploadPop?.(false);
        onOpen?.();
    };



    return (
        <>
            <div type="button" className="google-drive-popup-folder" data-tooltip-id="tab-dropdown-lesson-YT" style={{ opacity: youtubeLinks.length >= 1 && '0.5', cursor: youtubeLinks.length >= 1 && 'not-allowed' }} onClick={handleClick}>
                <div className="google-drive-image">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
                    </svg>
                </div>
                <p>Upload Link</p>
            </div>

            {youtubeLinks.length >= 1 && (
                <Tooltip
                    id="tab-dropdown-lesson-YT"
                    place="top"
                    content="You can only add one link at a time."
                    type="light"
                    effect="float"
                />
            )}
        </>
    );
}

/* ---------------- the popup modal (matches screenshot) ---------------- */

export function UploadLinkModal({ open, onClose, onSave, onGenerate }) {
    const [value, setValue] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [generated, setGenerated] = useState(false);

    const reset = () => {
        setValue("");
        setError("");
        setLoading(false);
        setGenerated(false);
    };

    const handleClose = () => {
        if (loading) return;
        reset();
        onClose?.();
    };

    const validate = () => {
        const result = parseVideoUrl(value);
        if (!result.valid) {
            setError(result.error);
            return null;
        }
        setError("");
        return result;
    };

    const handleSave = async () => {
        const result = validate();
        if (!result) return;
        try {
            setLoading(true);
            await onSave?.(result); // hand the normalized link up to LessonGenerator
            reset();
            onClose?.();
        } catch (err) {
            console.error("UploadLinkModal save failed:", err);
            setError("Couldn't save the link. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const canAct = value.trim().length > 0 && !loading;

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="upload-link-title">
            <div className="upload-link-modal" onClick={(e) => e.stopPropagation()}>
                <div className="upload-link-header">
                    <h3 id="upload-link-title">Upload Link</h3>
                    <button type="button" className="upload-link-close" onClick={handleClose}>
                        ✕
                    </button>
                </div>

                <p className="upload-link-subtitle">Drop YouTube Link</p>

                <div className={`upload-link-input-row ${error ? "has-error" : ""}`}>
                    <span className="upload-link-globe">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
                        </svg>
                    </span>
                    <input
                        type="text"
                        value={value}
                        placeholder="Paste Your URL here..."
                        onChange={(e) => {
                            setValue(e.target.value);
                            setError("");
                            setGenerated(false);
                        }}
                        onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();          // avoid implicit form submit if ever wrapped in <form>
                            if (!canAct) return;         // mirrors the button's disabled state
                            handleSave();
                        }}
                    />
                </div>

                {error && <p className="upload-link-error">{error}</p>}

                <div className="upload-link-actions">
                    <button
                        type="button"
                        className="upload-link-cancel"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="upload-link-save"
                        onClick={handleSave}
                        disabled={!canAct}
                    >
                        {/* Save */}
                        {loading ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}